import { http } from "./client";
import type { PageParams } from "@/types/api";
import type {
  Asset,
  AssetAssignment,
  AssetCategory,
  AssetCondition,
  AssetDashboardStats,
  AssetExitClearance,
  AssetExitClearanceItem,
  AssetIncident,
  AssetIncidentResolution,
  AssetIncidentStatus,
  AssetIncidentType,
  AssetMaintenance,
  AssetReportSummary,
  AssetRequest,
  AssetRequestStatus,
  AssetRequestType,
  AssetReturn,
  AssetStatus,
  AssetTransfer,
  AssignmentStatus,
  ClearanceItemStatus,
  ExitClearanceStatus,
  InspectionResult,
  MaintenanceStatus,
  TransferStatus,
} from "@/types/domain";

export type AssetCategoryFilters = PageParams;

export interface AssetFilters extends PageParams {
  status?: AssetStatus | "";
  condition?: AssetCondition | "";
  category_id?: string;
  location?: string;
  warranty_expiring_within_days?: number;
}

export interface AssetAssignmentFilters extends PageParams {
  status?: AssignmentStatus | "";
  asset_id?: string;
  employee_id?: string;
}

export interface AssetTransferFilters extends PageParams {
  status?: TransferStatus | "";
  asset_id?: string;
  employee_id?: string;
}

export interface AssetMaintenanceFilters extends PageParams {
  status?: MaintenanceStatus | "";
  asset_id?: string;
}

export interface ExitClearanceFilters extends PageParams {
  status?: ExitClearanceStatus | "";
  employee_id?: string;
}

export interface AssetRequestFilters extends PageParams {
  status?: AssetRequestStatus | "";
  employee_id?: string;
}

export interface AssetIncidentFilters extends PageParams {
  status?: AssetIncidentStatus | "";
  employee_id?: string;
  asset_id?: string;
}

export interface SubmitAssetRequestInput {
  employee_id?: string;
  category_id: string;
  request_type: AssetRequestType;
  justification: string;
  replacing_asset_id?: string;
}

export interface ReportAssetIncidentInput {
  asset_id: string;
  employee_id?: string;
  incident_type: AssetIncidentType;
  description: string;
  incident_date: string;
}

export interface AssetInput {
  asset_tag: string;
  category_id: string;
  name: string;
  brand?: string;
  model?: string;
  serial_number?: string;
  purchase_date?: string;
  purchase_cost?: number | null;
  vendor?: string;
  warranty_start?: string;
  warranty_end?: string;
  location?: string;
  condition?: AssetCondition;
  description?: string;
}

export const assetsApi = {
  // ---- categories ----
  listCategories: (filters: AssetCategoryFilters = {}) => http.getPage<AssetCategory>("/assets/categories", filters),
  createCategory: (values: { name: string; description?: string }) => http.post<AssetCategory>("/assets/categories", values),
  updateCategory: (id: string, values: Partial<{ name: string; description: string }>) =>
    http.put<AssetCategory>(`/assets/categories/${id}`, values),
  deleteCategory: (id: string) => http.delete<void>(`/assets/categories/${id}`),

  // ---- registry ----
  listAssets: (filters: AssetFilters = {}) => http.getPage<Asset>("/assets", filters),
  getAsset: (id: string) => http.get<Asset>(`/assets/${id}`),
  myAssets: () => http.get<AssetAssignment[]>("/employee/self/assets"),
  createAsset: (input: AssetInput) => http.post<Asset>("/assets", input),
  updateAsset: (id: string, input: Partial<AssetInput>) => http.put<Asset>(`/assets/${id}`, input),
  deleteAsset: (id: string) => http.delete<void>(`/assets/${id}`),

  // ---- assignment ----
  assignAsset: (id: string, employeeId: string, expectedReturnDate?: string, notes?: string) =>
    http.post<AssetAssignment>(`/assets/${id}/assign`, { employee_id: employeeId, expected_return_date: expectedReturnDate, notes }),
  acknowledgeAssignment: (id: string) => http.post<AssetAssignment>(`/assets/assignments/${id}/acknowledge`, {}),
  requestReturn: (id: string, notes?: string) => http.post<AssetAssignment>(`/assets/assignments/${id}/request-return`, { notes }),
  listAssignments: (filters: AssetAssignmentFilters = {}) => http.getPage<AssetAssignment>("/assets/assignments", filters),

  // ---- transfer ----
  requestTransfer: (assetId: string, toEmployeeId: string, notes?: string) =>
    http.post<AssetTransfer>(`/assets/${assetId}/transfer`, { to_employee_id: toEmployeeId, notes }),
  approveTransfer: (id: string) => http.put<AssetTransfer>(`/assets/transfers/${id}/approve`, {}),
  rejectTransfer: (id: string) => http.put<AssetTransfer>(`/assets/transfers/${id}/reject`, {}),
  completeTransfer: (id: string) => http.post<AssetTransfer>(`/assets/transfers/${id}/complete`, {}),
  listTransfers: (filters: AssetTransferFilters = {}) => http.getPage<AssetTransfer>("/assets/transfers", filters),

  // ---- maintenance ----
  scheduleMaintenance: (input: { asset_id: string; maintenance_type?: string; service_provider?: string; start_date?: string; warranty_covered?: boolean; notes?: string }) =>
    http.post<AssetMaintenance>("/assets/maintenance", input),
  listMaintenance: (filters: AssetMaintenanceFilters = {}) => http.getPage<AssetMaintenance>("/assets/maintenance", filters),
  startMaintenance: (id: string) => http.post<AssetMaintenance>(`/assets/maintenance/${id}/start`, {}),
  completeMaintenance: (id: string, cost?: number, description?: string) =>
    http.post<AssetMaintenance>(`/assets/maintenance/${id}/complete`, { cost, description }),
  cancelMaintenance: (id: string) => http.post<AssetMaintenance>(`/assets/maintenance/${id}/cancel`, {}),

  // ---- return ----
  returnAsset: (
    assignmentId: string,
    input: {
      condition_at_return?: AssetCondition;
      accessories_returned?: string;
      missing_items?: string;
      damage_details?: string;
      inspection_result: InspectionResult;
      notes?: string;
    },
  ) => http.post<AssetReturn>(`/assets/assignments/${assignmentId}/return`, input),
  listReturns: (assetId: string) => http.get<AssetReturn[]>(`/assets/${assetId}/returns`),

  // ---- exit clearance ----
  initiateExitClearance: (employeeId: string) => http.post<AssetExitClearance>("/assets/exit-clearances", { employee_id: employeeId }),
  getExitClearance: (id: string) =>
    http.get<{ clearance: AssetExitClearance; items: AssetExitClearanceItem[] }>(`/assets/exit-clearances/${id}`),
  listExitClearances: (filters: ExitClearanceFilters = {}) => http.getPage<AssetExitClearance>("/assets/exit-clearances", filters),
  resolveClearanceItem: (clearanceId: string, itemId: string, status: ClearanceItemStatus, notes?: string) =>
    http.put<void>(`/assets/exit-clearances/${clearanceId}/items/${itemId}`, { status, notes }),
  approveExitClearance: (id: string, department: "hr" | "it", override?: boolean, overrideReason?: string) =>
    http.put<AssetExitClearance>(`/assets/exit-clearances/${id}/approve`, { department, override, override_reason: overrideReason }),

  // ---- warranty ----
  warrantyExpiring: (days?: number) => http.get<Asset[]>("/assets/warranty/expiring", days ? { days } : undefined),

  // ---- dashboard ----
  dashboard: () => http.get<AssetDashboardStats>("/assets/dashboard"),

  // ---- reports ----
  reportSummary: () => http.get<AssetReportSummary>("/assets/reports/summary"),

  // ---- requests (Phase 5) ----
  submitRequest: (input: SubmitAssetRequestInput) => http.post<AssetRequest>("/assets/requests", input),
  listRequests: (filters: AssetRequestFilters = {}) => http.getPage<AssetRequest>("/assets/requests", filters),
  getRequest: (id: string) => http.get<AssetRequest>(`/assets/requests/${id}`),
  fulfillRequest: (id: string, assetId: string) => http.post<AssetRequest>(`/assets/requests/${id}/fulfill`, { asset_id: assetId }),
  cancelRequest: (id: string) => http.post<AssetRequest>(`/assets/requests/${id}/cancel`, {}),

  // ---- incidents (Phase 5) ----
  reportIncident: (input: ReportAssetIncidentInput) => http.post<AssetIncident>("/assets/incidents", input),
  listIncidents: (filters: AssetIncidentFilters = {}) => http.getPage<AssetIncident>("/assets/incidents", filters),
  getIncident: (id: string) => http.get<AssetIncident>(`/assets/incidents/${id}`),
  startIncidentReview: (id: string) => http.post<AssetIncident>(`/assets/incidents/${id}/review`, {}),
  resolveIncident: (id: string, resolution: AssetIncidentResolution, resolutionNotes?: string, recoveryAmount?: number) =>
    http.post<AssetIncident>(`/assets/incidents/${id}/resolve`, {
      resolution, resolution_notes: resolutionNotes, recovery_amount: recoveryAmount,
    }),
};
